/**
 * useAdvisoryPersonasForDomain — J5 advisory board persona hook
 *
 * Fetches actors of type `knowledge_persona` that are assigned to a given
 * domain. Used by DecisionFormDialog to show/hide the advisory consult button
 * and to surface the AdvisoryBoardEmpty state (GAP 8).
 *
 * Returns `{ personas, hasAny, isLoading, isError }`.
 * When `domainId` is falsy, returns hasAny=false without fetching.
 */

import { useRorosActors } from './actors';
import type { RorosActorDTO } from '@/types/roros/actors';

export interface AdvisoryPersonasResult {
  personas: RorosActorDTO[];
  hasAny: boolean;
  isLoading: boolean;
  isError: boolean;
}

/**
 * useAdvisoryPersonasForDomain
 *
 * @param domainId - the domain to scope the lookup to. If falsy, returns
 *   hasAny=false immediately without making a network request.
 */
export function useAdvisoryPersonasForDomain(domainId: string | null | undefined): AdvisoryPersonasResult {
  // IMPORTANT: The /api/v1/actors query schema uses camelCase `domainId` and
  // `type` (not snake_case `domain_id` / `actor_type`). The schema is .strict()
  // so unknown keys cause a Zod validation error → 400 → empty actor list →
  // consult button never appears. Always use the camelCase names here.
  const params = domainId
    ? { type: 'knowledge_persona', domainId, status: 'active' }
    : undefined;

  // Only fetch when we have a domainId — the enabled flag is handled via
  // undefined params which causes the query to run without filters if domainId
  // is set. We additionally guard with the enabled option.
  const query = useRorosActors(
    params as Record<string, string> | undefined,
  );

  if (!domainId) {
    return { personas: [], hasAny: false, isLoading: false, isError: false };
  }

  const rawData = query.data;
  // The actors endpoint can return: RorosActorDTO[] or { data: RorosActorDTO[] }
  const allActors: RorosActorDTO[] = Array.isArray(rawData)
    ? rawData
    : ((rawData as any)?.data ?? []);

  // Filter client-side for knowledge_persona type (belt-and-suspenders in case
  // the backend doesn't filter by actor_type param for older API versions).
  const personas = allActors.filter(
    (a) => a.actorType === 'knowledge_persona' || (a as any).actor_type === 'knowledge_persona',
  );

  return {
    personas,
    hasAny: personas.length > 0,
    isLoading: query.isLoading,
    isError: query.isError,
  };
}
